import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core'
import { CommonModule } from '@angular/common'
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { IonicModule, ModalController } from '@ionic/angular'
import { TranslocoModule, TranslocoService } from '@jsverse/transloco'
import { IpJournalCopyFormService } from './ip-journal-copy-form.service'
import { FORM_FIELD_NAMES, MODAL_ROLES, TRANSLATION_KEYS } from './ip-journal-copy-editor.constants'
import { ToasterService } from 'src/app/features/notification/toaster.service'
import { MultipleClientSelectComponent } from './multiple-client-select/multiple-client-select.component'
import { MultipleIPEntriesTreeComponent } from './multiple-ip-entries-tree/multiple-ip-entries-tree.component'
import { TreeNode } from 'primeng/api'
import { IPJournalCloneForm } from './ip-journal-clone-form.model'
import { NavService } from '../../../../navigation/nav.service'

@Component({
  selector: 'app-ip-journal-copy-action-editor',
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    ReactiveFormsModule,
    TranslocoModule,
    MultipleClientSelectComponent,
    MultipleIPEntriesTreeComponent,
  ],
  templateUrl: './ip-journal-clone-editor.component.html',
  styleUrl: './ip-journal-clone-editor.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IpJournalCloneEditorComponent implements OnInit {
  @Input() clientId: number

  @Output() saved = new EventEmitter<IPJournalCloneForm>()
  @Output() modalClosed = new EventEmitter<void>()

  private readonly fb = inject(FormBuilder)
  private readonly service = inject(IpJournalCopyFormService)
  private readonly transloco = inject(TranslocoService)
  private readonly modalCtrl = inject(ModalController)
  private readonly toaster = inject(ToasterService)
  private readonly navService = inject(NavService)

  form!: FormGroup<{
    readonly clientId: FormControl<number | null>
    readonly IPEntriesSelect: FormControl<TreeNode[] | null>
  }>

  ngOnInit(): void {
    this.initializeForm()
  }

  private initializeForm(): void {
    this.form = this.fb.group({
      [FORM_FIELD_NAMES.CLIENT_ID]: this.fb.control<number | null>(null, { validators: [Validators.required] }),
      [FORM_FIELD_NAMES.IP_ENTRIES_SELECT]: this.fb.control<TreeNode[] | null>(null, {
        validators: [Validators.required],
      }),
    }) as FormGroup<{
      readonly clientId: FormControl<number | null>
      readonly IPEntriesSelect: FormControl<TreeNode[] | null>
    }>
  }

  async submit(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched()
      return
    }

    const { clientId, IPEntriesSelect } = this.form.getRawValue()

    try {
      const saved = await this.service.saveFromTree({
        clientIds: (clientId ?? []) as number[],
        tree: IPEntriesSelect ?? [],
      })

      await this.toaster.simpleToast(this.transloco.translate(TRANSLATION_KEYS.TOAST_SAVE_SUCCESS))

      this.saved.emit(saved)
      const modal = await this.modalCtrl.getTop()
      if (modal) await modal.dismiss(saved)

      if (saved.clientIds.length === 1) {
        await this.navService.goToClientProfile(saved.clientIds[0])
      }
    } catch (error) {
      await this.toaster.errorToast(this.transloco.translate(TRANSLATION_KEYS.TOAST_SAVE_ERROR))
      console.error('Error copying IP entries:', error)
    }
  }

  onCancel(): void {
    this.modalClosed.emit()
    this.modalCtrl
      .getTop()
      .then((top) => {
        if (top) top.dismiss(undefined, MODAL_ROLES.CANCEL)
      })
      .catch(() => void 0)
  }
}
